import { useMemo, useState } from 'react'
import { Box, Checkbox, Divider, Menu, MenuItem as MuiMenuItem, Select, MenuItem, Stack, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { motion } from 'framer-motion'
import { primary } from '../../../theme/palette'
import type { LocationTreeNode, Site } from '@cmms/types'
import { useCategoryView } from './categories'
import { SearchableSelect } from '../../../components/SearchableSelect'
import { formatSiteName } from '../../../lib/siteDisplayName'
import { BulkActionBar } from '../../../components/BulkActionBar'
import type { useBulkSelection } from '../../../hooks/useBulkSelection'

type SortKey = 'name-asc' | 'name-desc'

export interface TreeContextAction {
  type: 'edit' | 'delete' | 'add-child'
  node: LocationTreeNode
}

interface Props {
  sites: Site[]
  selectedSiteId: string | null
  onSelectSite: (id: string) => void
  tree: LocationTreeNode[]
  selectedId: string | null
  selectedType: 'site' | 'location' | null
  onSelect: (id: string) => void
  onSelectSiteDetail: () => void
  onContextAction?: (action: TreeContextAction) => void
  search?: string
  /// Optional bulk-selection harness — when provided, each tree row
  /// renders a checkbox and the floating action bar appears. The page
  /// hands this in only for users with the `locations:delete`
  /// permission.
  bulk?: ReturnType<typeof useBulkSelection>
  /// Bulk delete — pass `undefined` (or omit) to hide the destructive
  /// button. The bulk-action bar itself still renders as long as at
  /// least one of `onBulkDelete` / `onBulkQrZip` is provided.
  onBulkDelete?: () => void
  bulkDeleting?: boolean
  /// Bulk QR-ZIP export — takes the currently-selected ids, generates
  /// one PNG per location matching the org's sticker template, then
  /// bundles them into a ZIP.
  onBulkQrZip?: () => void
  bulkQrZipping?: boolean
}

export function LocationTree({
  sites, selectedSiteId, onSelectSite, tree,
  selectedId, selectedType, onSelect, onSelectSiteDetail, onContextAction, search,
  bulk, onBulkDelete, bulkDeleting, onBulkQrZip, bulkQrZipping,
}: Props) {
  const safeTree = Array.isArray(tree) ? tree : []

  const [sort, setSort] = useState<SortKey>('name-asc')
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(safeTree.map((n) => n.id)))

  // Context menu state
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; node: LocationTreeNode } | null>(null)

  useMemo(() => {
    setExpanded((prev) => {
      const next = new Set(prev)
      safeTree.forEach((n) => next.add(n.id))
      if (selectedId) {
        const ancestors = findAncestorIds(safeTree, selectedId)
        ancestors.forEach((id) => next.add(id))
      }
      return next
    })
  }, [safeTree, selectedId])

  const matchIds = useMemo(() => {
    const q = search?.trim().toLowerCase()
    if (!q) return null
    const hits = new Set<string>()
    function walk(nodes: LocationTreeNode[]) {
      for (const n of nodes ?? []) { if (n.name.toLowerCase().includes(q!)) hits.add(n.id); walk(n.children ?? []) }
    }
    walk(safeTree)
    const visible = new Set<string>()
    function mark(nodes: LocationTreeNode[], ancestors: string[]): boolean {
      let any = false
      for (const n of nodes ?? []) {
        const childHit = mark(n.children ?? [], [...ancestors, n.id])
        if (hits.has(n.id) || childHit) { visible.add(n.id); ancestors.forEach((a) => visible.add(a)); any = true }
      }
      return any
    }
    mark(safeTree, [])
    return { hits, visible }
  }, [search, safeTree])

  const sorted = useMemo(() => {
    function s(nodes: LocationTreeNode[]): LocationTreeNode[] {
      return [...nodes]
        .sort((a, b) => sort === 'name-asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name))
        .map((n) => ({ ...n, children: s(n.children ?? []) }))
    }
    return s(safeTree)
  }, [safeTree, sort])

  function toggle(id: string) {
    setExpanded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next })
  }

  function handleContextMenu(e: React.MouseEvent, node: LocationTreeNode) {
    e.preventDefault()
    e.stopPropagation()
    setCtxMenu({ x: e.clientX, y: e.clientY, node })
  }

  function handleCtxAction(type: TreeContextAction['type']) {
    if (ctxMenu && onContextAction) {
      onContextAction({ type, node: ctxMenu.node })
    }
    setCtxMenu(null)
  }

  const activeSite = sites.find((s) => s.id === selectedSiteId)
  const isSiteSelected = selectedType === 'site'
  // Flat list of every id in the tree — drives the header "select all"
  // tristate. Walked once per render, cheap relative to the tree itself.
  const allIds = useMemo(() => {
    const out: string[] = []
    function walk(nodes: LocationTreeNode[]) {
      for (const n of nodes ?? []) { out.push(n.id); walk(n.children ?? []) }
    }
    walk(sorted)
    return out
  }, [sorted])

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, width: { xs: '100%', md: 360 }, flexShrink: 0,
      borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', bgcolor: 'background.paper' }}>

      <Stack spacing={1} sx={{ px: 3, py: 1.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        {sites.length > 1 && (
          <SearchableSelect
            value={selectedSiteId ?? ''}
            onChange={onSelectSite}
            options={sites}
            getValue={(s) => s.id}
            getLabel={formatSiteName}
            placeholder="Select site"
          />
        )}
        <Stack direction="row" alignItems="center" spacing={1}>
          {bulk && allIds.length > 0 && (
            <Checkbox size="small"
              checked={bulk.allSelected(allIds)}
              indeterminate={bulk.someSelected(allIds)}
              onChange={(e) => bulk.replaceWith(e.target.checked ? allIds : [])}
              sx={{ p: 0.5 }} />
          )}
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
            {bulk && bulk.count > 0 ? `${bulk.count} selected · Sort by` : 'Sort by'}
          </Typography>
          <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
            size="small" variant="standard" disableUnderline
            sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', '& .MuiSelect-select': { py: 0, pr: '20px !important' }, '& .MuiSelect-icon': { color: 'primary.main', fontSize: 18 } }}>
            <MenuItem value="name-asc" sx={{ fontSize: 13 }}>Name · A to Z</MenuItem>
            <MenuItem value="name-desc" sx={{ fontSize: 13 }}>Name · Z to A</MenuItem>
          </Select>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{countAll(safeTree)}</Typography>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1.25,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>

        {/* Site root node */}
        {activeSite && (
          <Box onClick={onSelectSiteDetail}
            sx={{
              position: 'relative', display: 'flex', alignItems: 'center',
              px: 2.5, py: 1.5, cursor: 'pointer',
              transition: 'background-color 120ms ease',
              bgcolor: isSiteSelected ? primary.selected : 'transparent',
              '&:hover': { bgcolor: isSiteSelected ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
              '&::before': isSiteSelected ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
              mb: 0.5,
            }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'primary.main', color: 'primary.contrastText', flexShrink: 0 }}>
                <BusinessOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }} noWrap>{formatSiteName(activeSite)}</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Site</Typography>
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{sorted.length} loc</Typography>
            </Stack>
          </Box>
        )}

        {sorted.length === 0 && (
          <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 28, color: 'text.secondary', opacity: 0.4, mb: 1 }} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              {search ? 'No locations match your search.' : 'No locations yet. Create one to get started.'}
            </Typography>
          </Box>
        )}
        {sorted.map((node, i) => (
          <TreeRow key={node.id} node={node} depth={1} ancestorContinues={[true]} isLastSibling={i === sorted.length - 1}
            expanded={expanded} onToggle={toggle} selectedId={selectedId} onSelect={onSelect} matchIds={matchIds}
            onContextMenu={handleContextMenu} bulk={bulk} />
        ))}
      </Box>
      {bulk && (onBulkDelete || onBulkQrZip) && (
        <BulkActionBar count={bulk.count} label="location"
          deleting={bulkDeleting}
          onClear={bulk.clear}
          // A user without delete permission still gets the bar for
          // QR export — the destructive button is hidden by the
          // hideDelete flag below rather than un-mounting the whole bar.
          onDelete={onBulkDelete ?? (() => {})}
          hideDelete={!onBulkDelete}
          extraActions={onBulkQrZip ? (
            <Button
              size="small" variant="contained"
              startIcon={bulkQrZipping
                ? <CircularProgress size={12} color="inherit" />
                : <QrCode2OutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={onBulkQrZip} disabled={bulkQrZipping}
              sx={{
                fontSize: 12.5, fontWeight: 700, borderRadius: 999, px: 1.75,
                bgcolor: 'primary.main', color: 'primary.contrastText',
                boxShadow: primary.glow,
                '&:hover': { bgcolor: 'primary.dark', boxShadow: primary.glowHover },
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {bulkQrZipping ? 'Generating PDF…' : 'Download QR PDF'}
            </Button>
          ) : undefined} />
      )}

      {/* Right-click context menu */}
      <Menu
        open={Boolean(ctxMenu)}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
        slotProps={{ paper: { sx: { minWidth: 200, borderRadius: 1.5 } } }}
      >
        <MuiMenuItem onClick={() => handleCtxAction('add-child')} sx={{ fontSize: 13 }}>
          <AddIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
          Add sub-location
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleCtxAction('edit')} sx={{ fontSize: 13 }}>
          <EditOutlinedIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
          Edit
        </MuiMenuItem>
        <Divider />
        <MuiMenuItem onClick={() => handleCtxAction('delete')} sx={{ fontSize: 13, color: 'error.main' }}>
          <DeleteOutlineIcon sx={{ fontSize: 16, mr: 1.5 }} />
          Delete
        </MuiMenuItem>
      </Menu>
    </Box>
  )
}

function countAll(nodes?: LocationTreeNode[] | null): number {
  if (!Array.isArray(nodes)) return 0
  return nodes.reduce((s, n) => s + 1 + countAll(n.children ?? []), 0)
}

function findAncestorIds(nodes: LocationTreeNode[], targetId: string, path: string[] = []): string[] {
  for (const n of nodes ?? []) {
    if (n.id === targetId) return [...path, n.id]
    const children = n.children ?? []
    if (children.length > 0) {
      const found = findAncestorIds(children, targetId, [...path, n.id])
      if (found.length > 0) return found
    }
  }
  return []
}

// ─── Row ────────────────────────────────────────────────────────────────────

const INDENT = 20; const RAIL = 12

function TreeRow({ node, depth, ancestorContinues, isLastSibling, expanded, onToggle, selectedId, onSelect, matchIds, onContextMenu, bulk }: {
  node: LocationTreeNode; depth: number; ancestorContinues: boolean[]; isLastSibling: boolean
  expanded: Set<string>; onToggle: (id: string) => void; selectedId: string | null; onSelect: (id: string) => void
  matchIds: { hits: Set<string>; visible: Set<string> } | null
  onContextMenu: (e: React.MouseEvent, node: LocationTreeNode) => void
  bulk?: ReturnType<typeof useBulkSelection>
}) {
  if (matchIds && !matchIds.visible.has(node.id)) return null
  const children = node.children ?? []
  const hasChildren = children.length > 0
  const isExpanded = expanded.has(node.id) || Boolean(matchIds)
  const isSelected = selectedId === node.id
  const cat = useCategoryView(node.type)
  const Icon = cat.Icon

  // ── Cascade select: checking a parent picks the whole subtree ──
  // Every id under (and including) this node. We compute it once per
  // render — trees are small enough that a walk is cheaper than trying
  // to memoize on a moving `node` reference. Leaves collapse to `[id]`
  // so leaf-row behaviour is identical to the old single-toggle path.
  const subtreeIds: string[] = []
  ;(function walk(n: LocationTreeNode) {
    subtreeIds.push(n.id)
    for (const c of n.children ?? []) walk(c)
  })(node)
  const allDescSelected  = bulk ? bulk.allSelected(subtreeIds)  : false
  const someDescSelected = bulk ? bulk.someSelected(subtreeIds) : false

  return (
    <Box>
      <Box
        onClick={() => { onSelect(node.id); if (hasChildren && !isExpanded) onToggle(node.id) }}
        onContextMenu={(e) => onContextMenu(e, node)}
        sx={{
          position: 'relative', display: 'flex', alignItems: 'stretch', pr: 2.5, minHeight: 42, cursor: 'pointer',
          transition: 'background-color 120ms ease',
          bgcolor: isSelected ? primary.selected : 'transparent',
          '&:hover': { bgcolor: isSelected ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
          '&::before': isSelected ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
        }}>
        {ancestorContinues.map((c, i) => (
          <Box key={i} sx={{ width: INDENT, flexShrink: 0, position: 'relative',
            '&::before': c ? { content: '""', position: 'absolute', left: RAIL, top: 0, bottom: 0, width: '1px', bgcolor: 'divider' } : undefined }} />
        ))}
        {depth > 0 && (
          <Box sx={{ width: INDENT, flexShrink: 0, position: 'relative',
            '&::before': { content: '""', position: 'absolute', left: RAIL, top: 0, bottom: isLastSibling ? '50%' : 0, width: '1px', bgcolor: 'divider' },
            '&::after': { content: '""', position: 'absolute', left: RAIL, top: '50%', width: INDENT - RAIL, height: '1px', bgcolor: 'divider' } }} />
        )}
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flex: 1, minWidth: 0, py: 1, pl: depth === 0 ? 3 : 1 }}>
          {bulk && (
            <Checkbox size="small"
              checked={allDescSelected}
              indeterminate={!allDescSelected && someDescSelected}
              onClick={(e) => {
                e.stopPropagation()
                // Checking a parent picks every descendant; unchecking
                // clears the whole subtree. Non-subtree selections in
                // other branches stay untouched.
                if (allDescSelected) {
                  const drop = new Set(subtreeIds)
                  bulk.replaceWith(bulk.ids.filter((id) => !drop.has(id)))
                } else {
                  bulk.replaceWith(Array.from(new Set([...bulk.ids, ...subtreeIds])))
                }
              }}
              onChange={() => {}}
              sx={{ p: 0.25, flexShrink: 0 }} />
          )}
          <Box onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id) }}
            sx={{ width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', flexShrink: 0, opacity: hasChildren ? 1 : 0 }}>
            <motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.18 }} style={{ display: 'inline-flex' }}>
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </motion.div>
          </Box>
          <Box sx={{ width: 30, height: 30, borderRadius: 1.25, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: isSelected ? cat.color.bgSel : cat.color.bg, color: cat.color.fg, flexShrink: 0 }}>
            <Icon sx={{ fontSize: 16 }} />
          </Box>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: isSelected ? 600 : 500, letterSpacing: '-0.005em' }} noWrap>{node.name}</Typography>
          {hasChildren && <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', opacity: 0.7, flexShrink: 0 }}>{children.length}</Typography>}
        </Stack>
      </Box>
      {hasChildren && isExpanded && children.map((child, i) => (
        <TreeRow key={child.id} node={child} depth={depth + 1} ancestorContinues={[...ancestorContinues, !isLastSibling]}
          isLastSibling={i === children.length - 1} expanded={expanded} onToggle={onToggle}
          selectedId={selectedId} onSelect={onSelect} matchIds={matchIds} onContextMenu={onContextMenu} bulk={bulk} />
      ))}
    </Box>
  )
}
